// Import Modules
import {
  Morandi,
  getDefaultCrop,
  getDefaultCropRatio,
  getFixedCrop,
  validNumber,
  CanvasFilters,
} from './canvas';
import { X, Y, WIDTH, HEIGHT } from './iu-constants';

const INCH_MM = 25.4;
const RIGHT_ANGLE = 90;
const DEF_BRIGHTNESS = 10;
const DEF_FX = 'colour';
const DEF_STRAIGHTEN = 0;
const DEF_ZOOM = 1;
const MAX_ZOOM = 10;
const MIN_ZOOM = 1;
const PORTRAIT = 0;
const LANDSCAPE = 1;

const ImageUtils = (() => {
  const zoomHelpers = (function () {
    const straightenStorage = {};
    const brightnessStorage = {};
    const zoomStorage = {};

    return {
      defaultCrop(photo, sizeArea) {
        const area = sizeArea ?? photo.size_area ?? photo.size?.area;
        const { raw_dimensions: rawDimensions, constraints = {} } = photo;

        let { w, h } = rawDimensions;

        const ratio = constraints.prism
          ? constraints.ratio // already rotated
          : getDefaultCropRatio(area[X] / area[Y], w, h);

        if (photo.straighten % (2 * RIGHT_ANGLE) === RIGHT_ANGLE) {
          [h, w] = [rawDimensions.w, rawDimensions.h];
        }

        return getFixedCrop(ratio, w, h);
      },
      getCrop(photo) {
        if (
          photo.crop &&
          typeof photo.crop === 'object' &&
          photo.crop.length == 4
        ) {
          return photo.crop;
        }

        if (photo.crop && typeof photo.crop === 'string') {
          return photo.crop.split(/,/).map((e) => parseInt(e));
        }
      },
      setStraighten(imageId, value) {
        value = Number(value);
        if (isNaN(value)) {
          value = DEF_STRAIGHTEN;
        }

        straightenStorage[imageId] = value;
      },
      getStraighten(imageId) {
        let value = DEF_STRAIGHTEN;

        if (straightenStorage.hasOwnProperty(imageId)) {
          value = straightenStorage[imageId];
        }

        return value;
      },
      setBrightness(imageId, value) {
        value = Number(value);
        if (isNaN(value)) {
          value = DEF_BRIGHTNESS;
        }

        brightnessStorage[imageId] = value;
      },
      getBrightness(imageId) {
        let value = DEF_BRIGHTNESS;

        if (brightnessStorage.hasOwnProperty(imageId)) {
          value = brightnessStorage[imageId];
        }

        return value;
      },
      setZoom(imageId, value) {
        value = Number(value);
        if (isNaN(value)) {
          value = DEF_ZOOM;
        }

        zoomStorage[imageId] = value;
      },
      getZoom(imageId) {
        let value = DEF_ZOOM;

        if (zoomStorage.hasOwnProperty(imageId)) {
          value = zoomStorage[imageId];
        }

        return value;
      },
      getZoomFromPhotoData(photo, sizeArea) {
        if (!photo.crop) {
          return DEF_ZOOM;
        }

        const defaultCrop = this.defaultCrop(photo, sizeArea);
        const crop = this.getCrop(photo);
        const zoom = defaultCrop[WIDTH] / crop[WIDTH];

        if (isNaN(zoom)) {
          return DEF_ZOOM;
        }

        if (zoom > MAX_ZOOM) {
          return MAX_ZOOM;
        }

        if (zoom < MIN_ZOOM) {
          return MIN_ZOOM;
        }

        return zoom;
      },
      hasZoom(imageId) {
        return zoomStorage.hasOwnProperty(imageId);
      },
      // The zoom slider runs on a 0–1 log-scale track: equal drags multiply
      // zoom by equal factors, so the low end gets fine-grained control
      // (position 0.25 → ~1.78x, 0.5 → ~3.16x) instead of a linear jump.
      sliderPositionToZoom(position) {
        position = Math.min(Math.max(Number(position), 0), 1);
        return MIN_ZOOM * (MAX_ZOOM / MIN_ZOOM) ** position;
      },
      zoomToSliderPosition(zoom) {
        zoom = Math.min(Math.max(Number(zoom), MIN_ZOOM), MAX_ZOOM);
        return Math.log(zoom / MIN_ZOOM) / Math.log(MAX_ZOOM / MIN_ZOOM);
      },
    };
  })();

  const imgHelpers = (function () {
    function isMsIe() {
      const ua = window.navigator.userAgent;
      const msie = ua.indexOf('MSIE ');
      return msie > -1 || !!navigator.userAgent.match(/Trident.*rv\:11\./);
    }

    function urlMunge(url) {
      if (typeof url === 'string') {
        return url.replace(/^https?:/, '');
      }
      try {
        console.warn('Attempt to clean non-string URL');
      } catch (e) {}
      return null;
    }

    function cleanPrismImageURL(src, filter) {
      if (!src) return src;
      var replace = `\/${filter}[a-z]*$`;
      var re = new RegExp(replace);
      return src.replace(re, '/colour');
    }

    function getDPI(max_mm, max_pixels) {
      return (INCH_MM * max_pixels) / max_mm;
    }

    function doStraighten(img, inRatio /* Not Used ! */, straighten) {
      let dup;
      let ratio;
      let a_ratio;
      let rh;
      let rw;
      let rotationValueRad;
      let scale;
      let scale_w;
      let ctx;
      const { width } = img;
      const { height } = img;

      ratio = width / height;
      a_ratio = height / width;

      rotationValueRad = straighten * (Math.PI / (2 * RIGHT_ANGLE));
      const absStraightenRad = Math.abs(rotationValueRad);
      rh =
        height /
        (ratio * Math.sin(absStraightenRad) + Math.cos(absStraightenRad));
      scale = Math.abs(height / rh);

      rw =
        width /
        (a_ratio * Math.sin(absStraightenRad) + Math.cos(absStraightenRad));
      scale_w = Math.abs(width / rw);
      if (scale_w > scale) {
        scale = scale_w;
      }

      dup = Morandi.cloneCanvas(img);
      ctx = dup.getContext('2d');
      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.rotate(rotationValueRad);
      ctx.scale(scale, scale);
      ctx.translate(width / -2, height / -2);
      ctx.drawImage(dup, 0, 0);
      ctx.restore();
      return dup;
    }

    function getPhotoCrop(photo) {
      const { crop } = photo;

      if (crop) {
        if (typeof crop === 'object') {
          return crop;
        }

        if (typeof crop === 'string') {
          return crop.split(/,/).map((e) => parseInt(e));
        }
      }

      return getDefaultCrop(
        photo.size_area[X] / photo.size_area[Y],
        photo.raw_dimensions.w,
        photo.raw_dimensions.h,
      );
    }

    function getPhotoDPI(photo) {
      let mm = photo.size_area[X];
      if (mm < photo.size_area[Y]) {
        mm = photo.size_area[Y];
      }

      const crop = getPhotoCrop(photo);
      let px = crop[WIDTH];
      if (px < crop[HEIGHT]) {
        px = crop[HEIGHT];
      }

      return getDPI(mm, px);
    }

    function doProcessPhoto(img, props, ratio, scale) {
      img = Morandi.cloneCanvasAtAngle(
        img,
        (4 * RIGHT_ANGLE - props.angle) % (4 * RIGHT_ANGLE),
      );

      const ctx = img.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      if (props.straighten) {
        img = doStraighten(img, ratio, props.straighten);
      }

      // Scale down for pixel-ops
      if (scale && scale > 0 && scale < 1.0) {
        img = Morandi.cloneAndCropCanvasAtScale(
          img,
          img.width * scale,
          img.height * scale,
          img.width / img.height,
        );
      }

      // console.log('process: %o', props);
      if (validNumber(props.gamma, 0.1, 2.0)) {
        img = CanvasFilters.gamma(img, props.gamma);
      }
      if (!!props.fx && props.fx !== 'colour') {
        img = CanvasFilters[props.fx](img, 1.0);
      }
      if (validNumber(props.saturation, 0, 1.0)) {
        img = CanvasFilters.greyscale(img, 1.0 - props.saturation);
      }

      if (scale && scale > 0 && scale < 1.0) {
        img = Morandi.cloneAndCropCanvasAtScale(
          img,
          img.width / scale,
          img.height / scale,
          img.width / img.height,
        );
      }

      return img;
    }

    function getPhotoOrientation(photo) {
      const { w: width, h: height } = photo.raw_dimensions;

      return width < height ? PORTRAIT : LANDSCAPE;
    }

    function getPhotoAspectRatio(photo, sizeArea) {
      const area = sizeArea ?? photo.size_area ?? photo.size?.area;
      const ratio = area[X] / area[Y];
      if (getPhotoOrientation(photo) === PORTRAIT) {
        return 1 / ratio;
      }

      return ratio;
    }

    return {
      getPhotoDPI,
      getPhotoCrop,
      doProcessPhoto,
      getPhotoAspectRatio,
      cleanPrismImageURL,
      urlMunge,
      isMsIe,
    };
  })();

  return {
    /* Helper Numbers */
    X,
    Y,
    WIDTH,
    HEIGHT,

    /* Constants */
    DEF_STRAIGHTEN,
    MAX_STRAIGHTEN: 20,
    MIN_STRAIGHTEN: -20,
    DEF_BRIGHTNESS,
    MAX_BRIGHT: 15,
    MIN_BRIGHT: 5,
    DEF_GAMMA: 1,
    DEF_FX,
    DEF_ZOOM,
    MAX_ZOOM,
    MIN_ZOOM,
    RIGHT_ANGLE,
    INCH_MM,
    MAX_PRINTS: 999,
    EL2_WIDTH: 790,
    EL2_HEIGHT: 640,
    CANVAS_W: 600,
    CANVAS_H: 600,

    /* Functions */
    zoomHelpers,
    imgHelpers,
  };
})();

// The export is unusual because of _ImageUtils's definition
// _ImageUtils can be easily refactored such that the below export looks normal
// e.g. export default _ImageUtils;
export default ImageUtils;
